import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface DebriefLearning {
  meeting_id: string;
  project_id: string;
  project_name: string;
  project_code: string | null;
  customer_name: string | null;
  production_lead_name: string | null;
  held_at: string | null;
  created_at: string;
  what_went_well: string | null;
  what_went_poorly: string | null;
}

interface RawDebriefRow {
  id: string;
  project_id: string;
  held_at: string | null;
  created_at: string;
  what_went_well: string | null;
  what_went_poorly: string | null;
  projects: {
    name: string;
    project_code: string | null;
    production_lead_id: string | null;
    customers: { name: string } | null;
  } | null;
}

/**
 * Henter alle debrief-rader på tvers av prosjekter, joined med projects +
 * customers. Production-lead-navn løses fra users-tabellen via en separat
 * map (vi unngår nested join på users som ikke har FK til projects).
 */
export function useDebriefLearnings() {
  return useQuery({
    queryKey: ["debrief-learnings"],
    queryFn: async (): Promise<DebriefLearning[]> => {
      const [{ data: meetings, error: meErr }, { data: users, error: usErr }] = await Promise.all([
        supabase
          .from("project_meetings")
          .select(
            "id, project_id, held_at, created_at, what_went_well, what_went_poorly, projects(name, project_code, production_lead_id, customers(name))",
          )
          .eq("meeting_type", "debrief")
          .or("what_went_well.not.is.null,what_went_poorly.not.is.null")
          .order("held_at", { ascending: false, nullsFirst: false }),
        supabase.from("users").select("id, full_name"),
      ]);

      if (meErr) throw new Error(meErr.message);
      if (usErr) throw new Error(usErr.message);

      const userMap = new Map<string, string>(
        (users ?? []).map((u: { id: string; full_name: string }) => [u.id, u.full_name]),
      );

      return ((meetings ?? []) as unknown as RawDebriefRow[]).map((m) => ({
        meeting_id: m.id,
        project_id: m.project_id,
        project_name: m.projects?.name ?? "Ukjent prosjekt",
        project_code: m.projects?.project_code ?? null,
        customer_name: m.projects?.customers?.name ?? null,
        production_lead_name: m.projects?.production_lead_id
          ? userMap.get(m.projects.production_lead_id) ?? null
          : null,
        held_at: m.held_at,
        created_at: m.created_at,
        what_went_well: m.what_went_well,
        what_went_poorly: m.what_went_poorly,
      }));
    },
    staleTime: 5 * 60 * 1000,
  });
}
